import { useLayoutEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react'

/**
 * A 6-digit code typed into six boxes, one digit each. Typing moves to the next box, Backspace on
 * an empty box goes back one, and pasting the whole code (or the phone's one-time-code suggestion)
 * fills every box at once. The value is the digits typed so far, never longer than `length`.
 */
export function CodeBoxes({
  value,
  onChange,
  length = 6,
  autoFocus = false,
  disabled = false,
  labelledBy,
}: {
  value: string
  onChange: (value: string) => void
  length?: number
  autoFocus?: boolean
  disabled?: boolean
  /** Id of the visible label, so a screen reader names the group. */
  labelledBy?: string
}) {
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  // The digits as of the last change, ahead of the re-render. Typing moves focus to the next box
  // straight away, and that box's onFocus must see the new digit - reading the prop there saw the
  // old, shorter code, decided the box was past the next empty one and sent focus back to the first.
  const latest = useRef(value)
  useLayoutEffect(() => {
    latest.current = value
  }, [value])

  function update(next: string) {
    latest.current = next
    onChange(next)
  }

  function focusBox(i: number) {
    boxes.current[Math.max(0, Math.min(length - 1, i))]?.focus()
  }

  /** Puts digits in from box i onward - one when typed, several when pasted or autofilled. */
  function fillFrom(i: number, typed: string) {
    const digits = typed.replace(/\D/g, '')
    if (!digits) return
    // Overwrites in place, so correcting a middle digit keeps the ones after it.
    const current = latest.current
    const next = (current.slice(0, i) + digits + current.slice(i + digits.length)).slice(0, length)
    update(next)
    focusBox(Math.min(i + digits.length, next.length))
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault()
      // Clears this box if it has a digit, otherwise the one before it - and everything after,
      // since the code is one string and cannot have a gap in the middle.
      const current = latest.current
      const at = current[i] ? i : i - 1
      if (at < 0) return
      update(current.slice(0, at))
      focusBox(at)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusBox(i - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusBox(i + 1)
    }
  }

  function onPaste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    fillFrom(i, e.clipboardData.getData('text'))
  }

  return (
    <div role="group" aria-labelledby={labelledBy} className="grid grid-cols-6 gap-2 sm:gap-3">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            boxes.current[i] = el
          }}
          // Only the first box offers the phone's code suggestion; it fills all six through fillFrom.
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          inputMode="numeric"
          // Up to `length`, so an autofilled code arriving in one box is not cut to one digit.
          maxLength={length}
          value={value[i] ?? ''}
          // A box past the next empty one sends focus back to where the next digit goes. Otherwise
          // its digit is selected, so typing replaces it rather than adding a second one.
          onFocus={(e) => {
            if (i > latest.current.length) focusBox(latest.current.length)
            else e.target.select()
          }}
          onChange={(e) => fillFrom(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={(e) => onPaste(i, e)}
          aria-label={`Digit ${i + 1} of ${length}`}
          className="input h-14 !px-0 text-center text-2xl font-bold sm:h-16"
        />
      ))}
    </div>
  )
}
